import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';


const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 1.5rem 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(circle at 50% 0%, rgba(48, 209, 88, 0.1), transparent 70%);
  }
`;

const RegisterCard = styled.div`
  position: relative;
  z-index: 1;
  background: rgba(14, 18, 28, 0.65);
  border-radius: 32px;
  width: 100%;
  max-width: 540px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: saturate(180%) blur(40px);
  -webkit-backdrop-filter: saturate(180%) blur(40px);
  box-shadow: 
    0 40px 100px -20px rgba(0, 0, 0, 0.7),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
`;

const RegisterHeader = styled.div`
  padding: 3.5rem 2.5rem 2rem;
  text-align: center;
  color: #fff;

  h1 {
    margin: 0 0 12px 0;
    font-size: clamp(2.2rem, 5vw, 2.5rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    background: linear-gradient(135deg, #fff 0%, rgba(255, 255, 255, 0.7) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.65);
    font-size: 1.05rem;
    font-weight: 400;
  }
`;

const RegisterForm = styled.form`
  padding: 1rem 2.5rem 3.5rem;
  color: #f5f8ff;
`;

const FormField = styled.div`
  margin-bottom: 24px;
  
  label {
    display: block;
    margin-bottom: 10px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    padding-left: 4px;

    &.required::after {
      content: ' *';
      color: #ff453a;
    }
  }
  
  input, select {
    width: 100%;
    padding: 16px 20px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 18px;
    font-size: 1rem;
    transition: all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
    background: rgba(255, 255, 255, 0.03);
    color: #fff;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.25);
    }
    
    &:focus {
      outline: none;
      border-color: rgba(48, 209, 88, 0.5); /* Green tint for register */
      background: rgba(255, 255, 255, 0.06);
      box-shadow: 
        0 0 0 4px rgba(48, 209, 88, 0.1),
        inset 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    
    &.error {
      border-color: #ff453a;
      background: rgba(255, 69, 58, 0.05);
      
      &:focus {
        box-shadow: 0 0 0 4px rgba(255, 69, 58, 0.1);
      }
    }
  }
  
  .error-message {
    color: #ff453a;
    font-size: 0.85rem;
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding-left: 4px;
    font-weight: 500;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const RegisterButton = styled.button`
  width: 100%;
  background: #30d158; /* iOS Green */
  color: white;
  border: none;
  padding: 18px;
  border-radius: 20px;
  font-size: 1.05rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
  margin: 32px 0 24px 0;
  box-shadow: 0 10px 30px -5px rgba(48, 209, 88, 0.4);

  &:hover {
    transform: translateY(-2px);
    background: #28c04d;
    box-shadow: 0 20px 40px -10px rgba(48, 209, 88, 0.5);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ButtonSpinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.8s cubic-bezier(0.4, 0.0, 0.2, 1) infinite; /* Smooth spin */

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const DividerText = styled.div`
  text-align: center;
  margin: 24px 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.9rem;
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  }
  
  span {
    font-weight: 500;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
  }
`;

const LoginLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-weight: 600;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.02);
  font-size: 1rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }
`;

const AlertMessage = styled.div`
  padding: 16px;
  border-radius: 18px;
  margin-bottom: 24px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 12px;
  line-height: 1.4;

  &.success {
    background: rgba(48, 209, 88, 0.15);
    color: #30d158;
    border: 1px solid rgba(48, 209, 88, 0.2);
  }

  &.error {
    background: rgba(255, 69, 58, 0.15);
    color: #ff453a;
    border: 1px solid rgba(255, 69, 58, 0.2);
  }
`;

const PasswordRequirements = styled.div`
  background: rgba(255, 255, 255, 0.02);
  padding: 16px;
  border-radius: 16px;
  margin-top: 12px;
  font-size: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.08);

  h4 {
    margin: 0 0 10px 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    font-weight: 600;
  }

  .requirement {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
    color: rgba(255, 255, 255, 0.5);
    transition: color 0.2s;

    &.met {
      color: #30d158;

      .icon {
        color: #30d158;
        transform: scale(1.1);
      }
    }

    .icon {
      color: rgba(255, 255, 255, 0.2);
      font-size: 0.9rem;
      transition: all 0.2s;
    }
  }
`;

const RegisterPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    selectedRole: 'BUYER'
  });

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPasswordReqs, setShowPasswordReqs] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Clear messages
    if (message) setMessage('');
  };

  const getPasswordRequirements = () => {
    const reqs = [
      { text: 'At least 8 characters', met: formData.password.length >= 8 },
      { text: 'Contains uppercase letter', met: /[A-Z]/.test(formData.password) },
      { text: 'Contains lowercase letter', met: /[a-z]/.test(formData.password) },
      { text: 'Contains number', met: /\d/.test(formData.password) }
    ];
    return reqs;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    } else {
      const reqs = getPasswordRequirements();
      const unmet = reqs.filter(req => !req.met);
      if (unmet.length > 0) {
        errors.password = 'Password does not meet requirements';
      }
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setMessage('');

    try {
      const registrationData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password
      };

      console.log('Registration data being sent:', registrationData);
      await authAPI.register(registrationData);

      setMessage('Registration successful! Please check your email to verify your account.');

      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Registration successful! Please sign in with your new account.'
          }
        });
      }, 2000);

    } catch (error) {
      console.error('Registration error:', error);
      setMessage(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <Helmet>
        <title>Create Account - TikPlat</title>
      </Helmet>

      <RegisterCard>
        <RegisterHeader>
          <h1>✨ Join TikPlat</h1>
          <p>Your gateway to amazing events starts here</p>
        </RegisterHeader>

        <RegisterForm onSubmit={handleSubmit}>
          {message && (
            <AlertMessage className={message.includes('successful') ? 'success' : 'error'}>
              <span role="img" aria-label="status">{message.includes('successful') ? '✅' : '⚠️'}</span>
              {message}
            </AlertMessage>
          )}

          <FormRow>
            <FormField>
              <label className="required">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={formErrors.firstName ? 'error' : ''}
                placeholder="John"
                autoComplete="given-name"
              />
              {formErrors.firstName && (
                <div className="error-message">
                  ⚠️ {formErrors.firstName}
                </div>
              )}
            </FormField>

            <FormField>
              <label className="required">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={formErrors.lastName ? 'error' : ''}
                placeholder="Doe"
                autoComplete="family-name"
              />
              {formErrors.lastName && (
                <div className="error-message">
                  ⚠️ {formErrors.lastName}
                </div>
              )}
            </FormField>
          </FormRow>

          <FormField>
            <label className="required">Email Address</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={formErrors.email ? 'error' : ''}
              placeholder="john@example.com"
              autoComplete="email"
            />
            {formErrors.email && (
              <div className="error-message">
                ⚠️ {formErrors.email}
              </div>
            )}
          </FormField>

          <FormField>
            <label className="required">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={formErrors.password ? 'error' : ''}
              placeholder="Create a strong password"
              autoComplete="new-password"
              onFocus={() => setShowPasswordReqs(true)}
              onBlur={() => setShowPasswordReqs(false)}
            />
            {formErrors.password && (
              <div className="error-message">
                ⚠️ {formErrors.password}
              </div>
            )}

            {(showPasswordReqs || formData.password) && (
              <PasswordRequirements>
                <h4>Password Requirements:</h4>
                {getPasswordRequirements().map((req, index) => (
                  <div key={index} className={`requirement ${req.met ? 'met' : ''}`}>
                    <span className="icon">{req.met ? '✓' : '○'}</span>
                    <span>{req.text}</span>
                  </div>
                ))}
              </PasswordRequirements>
            )}
          </FormField>

          <FormField>
            <label className="required">Confirm Password</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={formErrors.confirmPassword ? 'error' : ''}
              placeholder="Confirm your password"
              autoComplete="new-password"
            />
            {formErrors.confirmPassword && (
              <div className="error-message">
                ⚠️ {formErrors.confirmPassword}
              </div>
            )}
          </FormField>

          <RegisterButton type="submit" disabled={loading}>
            {loading && <ButtonSpinner aria-hidden="true" />}
            {loading ? 'Creating Account...' : 'Create Account'}
          </RegisterButton>

          <DividerText>
            <span>Already have an account?</span>
          </DividerText>

          <LoginLink to="/login">
            Sign In Instead
          </LoginLink>
        </RegisterForm>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default RegisterPage;
